import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useUpdateRouting } from '@/hooks/use-services';
import type { FlowType, Service } from '@/types/service';

interface LegacyMigrateBannerProps {
  /** Service mit Legacy-Setup (chain leer, gatewayServiceId vorhanden). */
  service: Service;
  /** Optional: erfolgreicher Migrate liefert den aktualisierten Service. */
  onMigrated?: (service: Service) => void;
}

/**
 * Spec 0216 §5.6 + EC-3: Single-Click-Migration eines Legacy-Service in
 * eine FallbackChain mit genau 1 Eintrag. Operator waehlt den FlowType,
 * der Klick uebernimmt die alte `gatewayServiceId` als Eintrag #1.
 */
export function LegacyMigrateBanner({ service, onMigrated }: LegacyMigrateBannerProps) {
  const { t } = useTranslation('routing');
  const [flowType, setFlowType] = useState<FlowType | ''>('');
  const mutation = useUpdateRouting(service.serviceId);

  const legacyId = service.gatewayServiceId;
  const handleMigrate = () => {
    if (!flowType || !legacyId) return;
    mutation.mutate(
      {
        fallbackChain: [{ flowType, gatewayServiceId: legacyId }],
        msisdnCollectionUrl: service.msisdnCollectionUrl ?? null,
      },
      {
        onSuccess: (updatedService) => {
          toast.success(t('migrate.toastSuccess'));
          onMigrated?.(updatedService);
        },
        onError: (err) => {
          const message = err instanceof Error ? err.message : t('migrate.toastErrorGeneric');
          toast.error(message);
        },
      },
    );
  };

  const submitDisabled = !flowType || !legacyId || mutation.isPending;

  return (
    <div
      className="mb-6 space-y-3 rounded-md border border-yellow-500/50 bg-yellow-50/70 p-4 text-yellow-950 dark:bg-yellow-950/20 dark:text-yellow-100"
      data-testid="legacy-migrate-banner"
    >
      <div className="flex items-start gap-2">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
        <div>
          <p className="text-sm font-semibold">{t('migrate.title')}</p>
          <p className="text-xs text-muted-foreground">{t('migrate.subtitle')}</p>
        </div>
      </div>

      <p className="text-sm">
        {t('migrate.legacyIdLabel')}{' '}
        <span className="font-mono">{legacyId ?? '—'}</span>
      </p>

      <div className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
        <div className="space-y-1.5">
          <Label htmlFor="migrate-flow-type">{t('migrate.flowTypeLabel')}</Label>
          <Select value={flowType} onValueChange={(v) => setFlowType(v as FlowType)}>
            <SelectTrigger id="migrate-flow-type">
              <SelectValue placeholder={t('migrate.flowTypePlaceholder')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="WAP_CLICK">WAP_CLICK</SelectItem>
              <SelectItem value="WEB_TAN">WEB_TAN</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button onClick={handleMigrate} disabled={submitDisabled}>
          {mutation.isPending ? t('migrate.buttonPending') : t('migrate.button')}
        </Button>
      </div>

      <p className="text-xs text-muted-foreground">{t('migrate.helpText')}</p>
    </div>
  );
}
